import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#090A0F] grain">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-[#232738] bg-[#0D0E14] relative overflow-hidden">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-violet-700/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[300px] w-[300px] rounded-full bg-blue-700/10 blur-3xl" />
        <img src="/brand/dopamine_logo_trasparente.png" alt="Dopamine Agency" className="relative w-[300px] h-auto select-none" draggable="false" data-testid="auth-logo" />
        <div className="relative">
          <h2 className="text-4xl xl:text-5xl font-extrabold tracking-tight leading-[1.05]">Il sistema operativo<br />della tua agenzia.</h2>
          <p className="text-slate-400 mt-4 max-w-md text-sm">Creator, account Telegram, chatter, fan, conversazioni e vendite — tutto collegato, in tempo reale.</p>
        </div>
        <p className="relative text-xs text-slate-600">Accesso riservato al personale autorizzato.</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <img src="/brand/dopamine_logo_trasparente.png" alt="Dopamine Agency" className="lg:hidden w-[220px] h-auto mb-8 select-none" draggable="false" data-testid="auth-logo-mobile" />
          <h1 className="text-3xl font-extrabold tracking-tight">{title}</h1>
          <p className="text-sm text-slate-400 mt-1 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login, notice } = useAuth();
  const navigate = useNavigate();
  const [area, setArea] = useState("staff");
  const [f, setF] = useState({ identifier: "", password: "", remember: true });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [see, setSee] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(f.identifier.trim(), f.password, f.remember, area);
      navigate(area === "creator" ? "/creator" : "/");
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Accedi" subtitle="DOPAMINE AGENCY LLC · Pannello gestionale">
      <div className="mb-5 grid grid-cols-2 rounded-lg border border-[#232738] bg-[#0D0E14] p-1" data-testid="login-area-tabs">
        {[["staff", "Staff"], ["creator", "Creator"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => { if (v !== area) setF((x) => ({ ...x, password: "" })); setArea(v); setErr(""); }} className={`rounded-md py-2 text-sm font-bold transition-colors ${area === v ? "bg-violet-600 text-white" : "text-slate-400 hover:text-white"}`} data-testid={`login-tab-${v}`}>{l}</button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {notice && !err && <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-testid="login-notice">{notice}</div>}
        <div><Label>Email o username</Label><Input className="mt-1.5 bg-[#12141D]" value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} data-testid="login-identifier-input" autoComplete="username" required /></div>
        <div><Label>Password</Label><div className="relative mt-1.5"><Input type={see ? "text" : "password"} className="bg-[#12141D] pr-10" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" autoComplete="current-password" required />
          <button type="button" onClick={() => setSee(!see)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white" aria-label={see ? "Nascondi password" : "Mostra password"} title={see ? "Nascondi password" : "Mostra password"} data-testid="login-password-eye">{see ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-slate-400"><Checkbox checked={f.remember} onCheckedChange={(v) => setF({ ...f, remember: !!v })} data-testid="login-remember-checkbox" />Ricordami</label>
          <Link to="/forgot-password" className="text-sm text-violet-400 hover:text-violet-300" data-testid="forgot-password-link">Password dimenticata?</Link>
        </div>
        {err && <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="login-error">{err}</div>}
        <Button type="submit" disabled={busy} className="w-full bg-violet-600 hover:bg-violet-500 text-white" data-testid="login-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
        </Button>
      </form>
    </Shell>
  );
}

export function ForgotPage() {
  const [id, setId] = useState("");
  const [done, setDone] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/auth/forgot-password", { identifier: id }); setDone(data.message); } catch (ex) { toast.error(errMsg(ex)); }
  };
  return (
    <Shell title="Recupero password" subtitle="Inserisci email o username del tuo account">
      {done ? <p className="text-sm text-slate-300" data-testid="forgot-done">{done}</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Input className="bg-[#12141D]" value={id} onChange={(e) => setId(e.target.value)} placeholder="email o username" data-testid="forgot-identifier-input" required />
          <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-500" data-testid="forgot-submit-button">Invia link di recupero</Button>
        </form>
      )}
      <Link to="/login" className="block mt-6 text-sm text-violet-400">← Torna al login</Link>
    </Shell>
  );
}

export function ResetPage() {
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token"), new_password: pw }); toast.success("Password aggiornata"); navigate("/login"); } catch (ex) { toast.error(errMsg(ex)); }
  };
  return (
    <Shell title="Nuova password" subtitle="Minimo 10 caratteri, lettere e numeri">
      <form onSubmit={submit} className="space-y-4">
        <Input type="password" className="bg-[#12141D]" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" required />
        <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-500" data-testid="reset-submit-button">Imposta password</Button>
      </form>
    </Shell>
  );
}

export function ForceChangePage() {
  const { setUser, logout } = useAuth();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return setErr("Le password non coincidono");
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      setUser(data);
      toast.success("Password aggiornata");
    } catch (ex) { setErr(errMsg(ex)); }
  };
  return (
    <Shell title="Cambio password obbligatorio" subtitle="Per sicurezza devi impostare una nuova password al primo accesso.">
      <form onSubmit={submit} className="space-y-4" data-testid="force-change-form">
        <div className="flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md p-2"><Lock className="h-3.5 w-3.5" />Minimo 10 caratteri con lettere e numeri</div>
        <Input type="password" placeholder="Password attuale" className="bg-[#12141D]" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="force-current-password" required />
        <Input type="password" placeholder="Nuova password" className="bg-[#12141D]" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="force-new-password" required />
        <Input type="password" placeholder="Conferma nuova password" className="bg-[#12141D]" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="force-confirm-password" required />
        {err && <div className="text-sm text-red-300" data-testid="force-change-error">{err}</div>}
        <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-500" data-testid="force-change-submit">Aggiorna password</Button>
        <button type="button" onClick={() => logout()} className="w-full text-xs text-slate-500 hover:text-slate-300">Esci</button>
      </form>
    </Shell>
  );
}
